import { $api, buildQuery, rethrowRequestError } from "../../shared/api";
import { useEffect, useRef, useState } from "react";
import type { AxiosResponse } from "axios";

export type DownloadData = {
  download_url: string;
  filename?: string;
};

const getFilename = (header: string | null) => {
  if (!header) return "download";
  const match = /filename\*?=(?:UTF-8''|")?([^";]+)/i.exec(header);
  return match?.[1]?.trim() ?? "download";
};

const isFileResponse = (contentType: string, disposition: string | null) => {
  const type = contentType.toLowerCase();
  return (
    disposition?.toLowerCase().includes("attachment") ||
    type.includes("spreadsheetml")
  );
};

const headerValue = (response: AxiosResponse<Blob>, name: string) => {
  const headers = response.headers;
  const value =
    headers && "get" in headers && typeof headers.get === "function"
      ? headers.get(name)
      : headers?.[name];
  return typeof value === "string" ? value : "";
};

const parseDownloadResponse = async (
  response: AxiosResponse<Blob>,
): Promise<DownloadData> => {
  const contentType = headerValue(response, "content-type");
  const disposition = headerValue(response, "content-disposition") || null;
  const blob = response.data;

  if (response.status >= 400) {
    if (contentType.includes("application/json")) {
      const body = JSON.parse(await blob.text());
      throw new Error(
        body?.errors?.[0]?.error_message || body?.detail || response.statusText,
      );
    }
    throw new Error((await blob.text()) || response.statusText);
  }

  if (contentType.includes("application/json")) {
    const body = JSON.parse(await blob.text());

    if (body.errors?.length) {
      throw new Error(body.errors[0]?.error_message || "Download failed");
    }

    const downloadUrl = body?.data?.download_url;
    if (!downloadUrl) {
      throw new Error("Has no download_url in network response");
    }

    return { download_url: downloadUrl };
  }

  if (isFileResponse(contentType, disposition)) {
    return {
      download_url: URL.createObjectURL(blob),
      filename: getFilename(disposition),
    };
  }

  throw new Error(`Unsupported response type: ${contentType}`);
};

const runDownloadRequest = async ({
  query,
  queryVariables,
  variables,
  abortController,
  method = "POST",
}: {
  query: string;
  queryVariables?: Record<string, unknown>;
  variables?: Record<string, unknown>;
  abortController?: AbortController;
  method?: "GET" | "POST" | "DELETE";
}) => {
  const url = buildQuery(query, queryVariables ?? {});
  const isPost = method === "POST";
  try {
    const response = await $api.request<Blob>({
      url,
      method,
      ...(isPost && { data: variables ?? {} }),
      responseType: "blob",
      signal: abortController?.signal,
      validateStatus: (status) => status !== 401,
      headers: isPost ? { "Content-Type": "application/json" } : undefined,
    });
    const data = await parseDownloadResponse(response);
    return { data };
  } catch (error) {
    rethrowRequestError(error);
  }
};

export const useFetchDownload = ({
  query,
  optionalArray = [],
}: {
  query: string;
  optionalArray?: unknown[];
  autoDownload?: boolean;
}) => {
  const [data, setData] = useState<DownloadData | null>(null);
  const [isLoading, setIsLoading] = useState<boolean | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const abortController = useRef<AbortController | null>(null);

  const fetchDownload = async (queryVariables = {}, variables = {}) => {
    if (abortController.current) abortController.current.abort();
    abortController.current = new AbortController();

    setIsLoading(true);
    setError(null);

    try {
      const result = await runDownloadRequest({
        query,
        queryVariables,
        variables,
        abortController: abortController.current,
      });
      setData(result.data);
    } catch (err: unknown) {
      if (err instanceof Error && err.name === "AbortError") return;
      setError(err instanceof Error ? err : new Error("Download failed"));
    } finally {
      setIsLoading(false);
    }
  };

  const clearData = () => {
    if (abortController.current) {
      abortController.current.abort();
      abortController.current = null;
    }
    setData(null);
  };

  useEffect(() => {
    if (optionalArray.length) clearData();
  }, optionalArray);

  const clearError = () => setError(null);

  return [fetchDownload, { isLoading, data, error, clearError, clearData }] as const;
};
